import { useCallback, useEffect, useState } from 'react';
import {
  supervisors,
  type Category,
  type Site,
  type Supervisor,
} from '../lib/api.ts';
import { ScopePicker } from './ScopePicker.tsx';

interface Props {
  readonly sites: readonly Site[];
  /** Who I am, so only the owner is offered the handover. */
  readonly meId?: string | null;
  readonly categories: readonly Category[];
}

export const Supervisors = (
  { sites, categories, meId }: Props,
) => {
  const [list, setList] = useState<Supervisor[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [invite, setInvite] = useState('');
  const [editing, setEditing] = useState<Supervisor | null>(null);
  const [busy, setBusy] = useState(false);
  const [chatOnly, setChatOnly] = useState(false);
  const [asAdmin, setAsAdmin] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    supervisors.list().then(setList).catch(() => setList([]));
  }, []);

  useEffect(refresh, [refresh]);

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const made = await supervisors.create({
        email: email.trim(),
        name: name.trim(),
        siteIds,
        categoryIds,
        chatOnly,
        role: asAdmin ? 'admin' : 'supervisor',
      });
      // Shown once, never stored. Nobody, including this admin, learns
      // the supervisor's password.
      setInvite(window.location.origin + made.inviteUrl);
      setEmail('');
      setName('');
      refresh();
    } catch {
      setError('Could not create that account. Is the email already used?');
    } finally {
      setBusy(false);
    }
  };

  const saveScope = async () => {
    if (!editing) return;
    setBusy(true);
    setError('');
    try {
      await supervisors.setScope(editing.id, siteIds, categoryIds, {
        chatOnly,
        name: name.trim() || undefined,
      });
      setEditing(null);
      setSiteIds([]);
      setCategoryIds([]);
      refresh();
    } catch {
      setError('Could not update that scope.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Withdraw every trusted browser and live session for one person.
   *
   * Confirmed by name: this is reached from a list, and the row above
   * the one you meant is a different colleague.
   */
  const signOutEverywhere = async (s: Supervisor) => {
    if (!window.confirm(
      `Sign ${s.name} out of everything?\n\n`
      + 'They will need their password and a two-factor code again '
      + 'on every device.')) return;

    await supervisors.revokeTrust(s.id).catch(() => undefined);
    refresh();
  };


  // Only the person who holds the account is offered the handover,
  // which is the entire protection: a colleague cannot take it.
  const iAmOwner = list.some(
    (u) => u.id === meId && u.is_account_owner,
  );

  const handAccountOver = async (s: Supervisor) => {
    if (!window.confirm(
      `Make ${s.name} the account owner?\n\n`
      + 'You will no longer hold the account, and only they will be '
      + 'able to hand it back.')) return;

    await supervisors.makeOwner(s.id).catch(() => undefined);
    refresh();
  };

  const startEdit = (s: Supervisor) => {
    setEditing(s);
    // Seed from the person being edited. Left on the create form's
    // defaults, saving coverage would have quietly cleared their
    // chat-only flag and left the name box empty.
    setName(s.name);
    setChatOnly(s.chat_only === true);
    // Names come back from the API, not ids -- map them so existing
    // coverage is preselected rather than silently wiped on save.
    setSiteIds(
      sites.filter((x) => s.sites.includes(x.name)).map((x) => x.id),
    );
    setCategoryIds(
      categories.filter((x) => s.categories.includes(x.name)).map((x) => x.id),
    );
  };

  if (editing) {
    return (
      <div className="noticeWrap">
        {error && <p className="err">{error}</p>}
        <section className="composer">
          <p className="mono">Coverage for {editing.name}</p>
          <label className="fieldLab" htmlFor="renameWho">Name</label>
          <input
            id="renameWho"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
          <ScopePicker
            sites={sites}
            categories={categories}
            siteIds={siteIds}
            categoryIds={categoryIds}
            onSites={setSiteIds}
            onCategories={setCategoryIds}
          />
          <p className="hint" style={{ marginTop: 14 }}>
            Saving signs them out, so the new scope applies immediately
            rather than at their next login.
          </p>
          <div className="noticeRow">
            <label className="check">
          <input
            type="checkbox"
            checked={chatOnly}
            onChange={(e) => setChatOnly(e.target.checked)}
          />
          Chat only
        </label>
        <button className="primary" disabled={busy} onClick={saveScope}>
              {busy ? 'Saving...' : 'Save coverage'}
            </button>
            <button onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="noticeWrap">
      {error && <p className="err">{error}</p>}

      {invite && (
        <section className="inviteBox">
          <p className="mono">Send this link &mdash; shown once</p>
          <code className="inviteLink">{invite}</code>
          <div className="noticeRow">
            <button
              onClick={() => void navigator.clipboard?.writeText(invite)}
            >
              Copy link
            </button>
            <button onClick={() => setInvite('')}>Done</button>
          </div>
        </section>
      )}

      <section className="composer">
        <p className="mono">Add someone</p>
        <div className="noticeRow">
          <input
            value={name}
            placeholder="Full name"
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="email"
            value={email}
            placeholder="Work email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>


        {/* The one thing that changes what this account is, so it
            belongs on the form that creates it rather than inside a
            coverage editor nobody opens until later. */}
        <label className="check kindPick">
          <input
            type="checkbox"
            checked={asAdmin}
            onChange={(e) => setAsAdmin(e.target.checked)}
          />
          <span>
            <b>Full access</b>
            <em>
              Every site, every category, and all settings including
              billing and adding people. Site and category ticks below
              do not apply.
            </em>
          </span>
        </label>

        <label className="check kindPick">
          <input
            type="checkbox"
            checked={chatOnly}
            onChange={(e) => setChatOnly(e.target.checked)}
          />
          <span>
            <b>Chat only</b>
            <em>
              Answers messages from sites and nothing else. No board,
              no notices, no tickets, no settings.
            </em>
          </span>
        </label>

        <div style={{ marginTop: 14 }}>
          <ScopePicker
            sites={sites}
            categories={categories}
            siteIds={siteIds}
            categoryIds={categoryIds}
            onSites={setSiteIds}
            onCategories={setCategoryIds}
          />
        </div>

        <button
          className="primary"
          style={{ marginTop: 14 }}
          disabled={busy || !name.trim() || !email.trim()}
          onClick={create}
        >
          {busy ? 'Creating...' : 'Create and get invite link'}
        </button>
      </section>

      <p className="mono covHead">Supervisors &mdash; {list.length}</p>
      {list.length === 0 ? (
        <p className="empty">Nobody added yet.</p>
      ) : (
        <ul className="covList">
          {list.map((s) => (
            <li key={s.id}>
              <span>
                {s.name}
                <span className="covWhen"> {s.email}</span>
                {s.pending_setup && (
                  <span className="pinAck"> not set up yet</span>
                )}
              </span>
              <span className="covWhen">
                {s.role === 'admin' ? (
                  <b className="ownerTag">full access</b>
                ) : s.sites.length === 0
                    || s.categories.length === 0 ? (
                  <b className="pinAck">sees nothing</b>
                ) : (
                  <>
                    {s.sites.length} site{s.sites.length === 1 ? '' : 's'}
                    {' / '}
                    {s.categories.join(', ')}
                  </>
                )}
                {s.is_account_owner ? (
                  <b className="ownerTag">account owner</b>
                ) : s.role === 'admin' && iAmOwner ? (
                  <button
                    className="linkish"
                    onClick={() => void handAccountOver(s)}
                  >
                    Make account owner
                  </button>
                ) : null}
                <button className="linkish" onClick={() => startEdit(s)}>
                  Edit coverage
                </button>
                <button
                  className="linkish"
                  onClick={() => void signOutEverywhere(s)}
                >
                  Sign out everywhere
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
